<script setup lang="ts">
import {
  computedAsync,
  useElementSize,
  useIntersectionObserver,
} from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';

import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';
import { PriceGuaranteeBanner } from '@apps/main/new-desktop/modules/priceGuarantee';
import {
  useBrowserStore,
  useCustomerFlowStore,
  useRecipeStore,
  useSembonusStore,
  useStatsigEventsStore,
  useUserStore,
} from '@apps/main/new-desktop/core/stores';
import { SembonusBanner } from '@apps/main/new-desktop/modules/sembonus';

import { AppButton, AppLinkButton, AppFlicker, AppSvgIcon } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { useGlobalCurrencyStore } from '../../../../core/stores/globalCurrencyStore';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from '../../stores';

interface TripSummaryColumnFooterProps {
  totalPrice: number;
  grossPrice: number | undefined;
  totalAddonsPrice: number;
  currency: string;
  paxes: string;
  travelDates: string;
  shouldShowShareButton: boolean;
}

const props = defineProps<TripSummaryColumnFooterProps>();

interface TripSummaryColumnFooterEmits {
  (eventName: 'total-price-clicked'): void;
}

const emits = defineEmits<TripSummaryColumnFooterEmits>();

const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripSummaryStore = useTripSummaryStore();
const globalCurrencyStore = useGlobalCurrencyStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const customerFlowStore = useCustomerFlowStore();
const recipeStore = useRecipeStore();
const userStore = useUserStore();
const sembonusStore = useSembonusStore();

const tripBookButtonRef = useTemplateRef('tripBookButtonRef');
const tripSummaryColumnFooterRef = useTemplateRef('tripSummaryColumnFooterRef');
const { width } = useElementSize(tripSummaryColumnFooterRef);
const bookButtonSizeThreshold = 480;

const bookButtonSize = computed(() =>
  width.value < bookButtonSizeThreshold ? 'medium' : 'large'
);

const shouldShowTripSummaryPreCheckout = computedAsync<boolean>(
  () => isExperimentAsync('trip_summary_pre_checkout_experiment'),
  false
);

function openTripSummary(): void {
  if (browserStore.isLargerThan1199px) {
    statsigEventsStore.logTripOverviewTripSummaryPanelOpened();
    emits('total-price-clicked');
  }
}

function handleBookClick(): void {
  if (shouldShowTripSummaryPreCheckout.value) {
    tripOverviewPanelsStore.openTripPriceSummaryPreCheckoutPanel();
  } else {
    if (
      customerFlowStore.isFlightOnlyFromMarketplaceFlow &&
      !recipeStore.hotelOnlyRecipe.length &&
      tripOverviewModalsStore.accommodationUpsellModalOpenCounter === 0
    ) {
      tripOverviewModalsStore.openAccommodationUpsellModal();
    } else {
      tripSummaryStore.onBookClicked();
    }
  }
}

useIntersectionObserver(
  tripBookButtonRef,
  (entries: IntersectionObserverEntry[]) => {
    if (entries[0]) {
      tripSummaryStore.isTripSummaryVisible = entries[0].isIntersecting;
    }
  }
);
</script>

<template>
  <div ref="tripSummaryColumnFooterRef" class="trip-summary-column-footer">
    <div class="banners">
      <SembonusBanner
        v-if="sembonusStore.shouldShowSembonusBanner"
        :stamps-to-earn="recipeStore.totalBookableNights"
        :is-logged-in="userStore.isUserLoggedIn"
        @open-sembonus-modal="tripOverviewModalsStore.openSembonusModal()"
      />
      <PriceGuaranteeBanner
        @learn-more="tripOverviewModalsStore.openPriceGuaranteeModal"
      />
    </div>

    <div class="trip-details">
      <div v-if="browserStore.isSmallerThan1200px" class="trip-general-info">
        <div class="section">
          <AppSvgIcon class="icon" name="peopleOutlined" />
          {{ props.paxes }}
        </div>
        <div v-if="props.travelDates.length" class="section">
          <AppSvgIcon name="calendarOutlined" />
          {{ props.travelDates }}
        </div>
        <AppFlicker v-else width="spacing-96" height="spacing-20" />
      </div>

      <div class="trip-summary">
        <DealCodeTag v-if="props.grossPrice" />
        <div
          v-if="tripOverviewPageStore.anyItineraryLoaded"
          class="price-section"
        >
          <div v-if="props.grossPrice" class="gross-price">
            {{
              globalCurrencyStore.formatMoney(
                props.grossPrice + props.totalAddonsPrice,
                props.currency
              )
            }}
          </div>
          <div
            class="total-price"
            :class="{ 'with-discount': props.grossPrice }"
          >
            {{
              globalCurrencyStore.formatMoney(
                props.totalPrice + props.totalAddonsPrice,
                props.currency
              )
            }}
          </div>
        </div>
        <div v-else class="price-section-loader">
          <AppFlicker
            v-if="props.grossPrice"
            width="spacing-48"
            height="spacing-32"
          />
          <AppFlicker width="spacing-96" height="spacing-40" />
        </div>

        <div class="total-price-text">
          {{ $t('planner.total_price_for_trip') }}
        </div>
        <AppLinkButton
          v-if="browserStore.isLargerThan1199px"
          class="trip-summary-link-button"
          :text="$t('planner.show_trip_summary')"
          size="m"
          @click="openTripSummary"
        />
      </div>
    </div>

    <div ref="tripBookButtonRef" class="trip-summary-actions">
      <AppButton
        type="important"
        :size="bookButtonSize"
        :wide="true"
        :state="tripSummaryStore.bookingButtonState"
        @click="handleBookClick"
      >
        {{
          shouldShowTripSummaryPreCheckout
            ? $t('planner.show_summary')
            : $t('planner.booking_proceed')
        }}
      </AppButton>

      <AppButton
        v-if="props.shouldShowShareButton"
        type="text"
        state="enabled"
        size="medium"
        :wide="true"
        @click="tripSummaryStore.openShareWidget"
      >
        {{ $t('planner.share_or_save_trip') }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column-footer {
  display: flex;
  flex-direction: column;
  container-name: tripSummaryColumnFooter;
  container-type: inline-size;

  @include for-less-or-equal-than-1199-px {
    padding: var(--spacing-16);
  }

  .banners {
    align-self: center;
    width: 100%;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
    padding: var(--spacing-8);
    background-color: var(--color-neutral-10);
    border-radius: var(--border-radius-8);

    @include for-less-or-equal-than-1199-px {
      margin-bottom: var(--spacing-24);
      gap: var(--spacing-8);
    }
  }

  .trip-details {
    padding-top: var(--spacing-12);
    @include for-less-or-equal-than-1199-px {
      padding-top: 0;
    }
    margin-bottom: var(--spacing-16);
    display: flex;
    gap: var(--spacing-8);

    @container tripSummaryColumnFooter (max-width: 500px) {
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-24);
    }

    .trip-general-info {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: var(--spacing-12);

      @container tripSummaryColumnFooter (max-width: 500px) {
        flex-direction: row;
        gap: var(--spacing-24);
      }

      .section {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
        color: var(--color-neutral-70);
        @include font-label-m-light;

        .icon {
          color: var(--color-soft-black);
        }
      }
    }

    .trip-summary {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-end;
      gap: var(--spacing-4);

      @container tripSummaryColumnFooter (max-width: 500px) {
        align-items: center;
      }

      .price-section {
        display: flex;
        gap: var(--spacing-8);

        .gross-price {
          @include font-heading-4;
          color: var(--color-neutral-70);
          align-self: center;
          text-decoration: line-through;
        }

        .total-price {
          @include font-heading-4;
          color: var(--color-neutral-70);

          &.with-discount {
            color: var(--color-error-dark);
          }
        }
      }

      .total-price-text {
        @include font-label-s-light;
        color: var(--color-neutral-70);
        margin-bottom: var(--spacing-4);
      }

      .price-section-loader {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
      }

      .trip-summary-link-button {
        color: var(--color-neutral-70);
      }
    }
  }

  .trip-summary-actions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
  }
}
</style>
